import react, { useEffect, useState } from 'react'
import Slider from '@mui/material/Slider';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { FaArrowLeftLong } from "react-icons/fa6";
import RangeChart from './AdminCharts/RangeChart';
import ConfirmBox from '../../ConfirmBox';
import { BackIcon, CrownIcon } from '../../../assets/icons/iconshere';
import axios from 'axios';
import ProPopup from '../../layouts/ProPopup';

const initialRanges = [
    {
        key: "Time",
        value: 0,
        id: "time",
        rangeid: "time-range",
        thumbid: "time-thumb"
    },
    {
        key: "Critical",
        value: 0,
        id: "crtical-steps",
        rangeid: "crtical-range",
        thumbid: "crtical-thumb"
    },
    {
        key: "Errors",
        value: 0,
        id: "errors",
        rangeid: "errors-range",
        thumbid: "errors-thumb"
    },
    {
        key: "Quiz",
        value: 0,
        id: "quiz",
        rangeid: "quiz-range",
        thumbid: "quiz-thumb"
    }
];

const EditCriteria = () => {
    let params = useParams();
    let navigate = useNavigate();
    const location = useLocation();
    const moduleUUID = params.id;
    const moduleName = location?.state?.name || null;
    const configData = useSelector(state => state.configSlice);
    const userData = useSelector(state => state.userSlice);
    const [setCriteria, ShowSetCriteria] = useState(false);
    const [ranges, setRanges] = useState(initialRanges);
    const [minimumSuccess, setMinimumSuccess] = useState(null);
    const [maxHintAllowed, setMaxHintAllowed] = useState(null);
    const [min_score_to_pass, setMin_score_to_pass] = useState(null)
    const [error, setError] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openPro, setOpenPro] = useState(false);
    const [userPlan, setUserPlan] = useState(null);

    useEffect(() => {
        userData?.plans?.data && setUserPlan(userData.plans.data);
    }, [userData]);

    const handleSaveRanges = (_passingCriteriaData) => {
        let _initialRanges = initialRanges.map(range => {
            let updatedValue = range.value;
            switch (range.id) {
                case "time":
                    updatedValue = _passingCriteriaData.time || range.value;
                    break;
                case "crtical-steps":
                    updatedValue = _passingCriteriaData.critical || range.value;
                    break;
                case "errors":
                    updatedValue = _passingCriteriaData.errors || range.value;
                    break;
                case "quiz":
                    updatedValue = _passingCriteriaData.quiz || range.value;
                    break;
                default:
                    break;
            }
            return { ...range, value: updatedValue };
        });

        setMin_score_to_pass(_passingCriteriaData.min_score_to_pass);
        setMinimumSuccess(_passingCriteriaData.minimumSuccess);
        setMaxHintAllowed(_passingCriteriaData.maxHintAllowed);
        setRanges(_initialRanges);
    }

    const getModulePassingCriteria = async () => {
        try {
            let { data } = await axios.get(`${configData.config.API_URL}/api/projects/passing/criteria/${moduleUUID}`, {
                headers: {
                    Authorization: `Bearer ${userData.token}`,
                    "Content-Type ": "application/json"
                }
            });

            let _passingCriteriaData = data.data.passingCriteria;
            handleSaveRanges(_passingCriteriaData);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        getModulePassingCriteria();
    }, [moduleUUID])


    const handleChange = (index, newValue) => {

        const newValueFraction = newValue / 100;

        const currentTotal = ranges.reduce((total, range, i) => {
            return i === index ? total : total + range.value;
        }, 0);

        if (currentTotal + newValueFraction > 1) return;

        const newRanges = ranges.map((range, i) => (
            i === index ? { ...range, value: newValueFraction } : range
        ));

        setRanges(newRanges);
    };



    const SaveCriteria = async () => {
        try {
            setErrorMsg(null);
            ShowSetCriteria(false);
            setLoading(true);

            let _bodyData = {
                passingCriteria: {
                    "quiz": ranges.find(e => e.id === "quiz").value,
                    "time": ranges.find(e => e.id == "time").value,
                    "errors": ranges.find(e => e.id == "errors").value,
                    "critical": ranges.find(e => e.id == "crtical-steps").value,
                    "minimumSuccess": minimumSuccess,
                    "maxHintAllowed": maxHintAllowed || null,
                    "min_score_to_pass": min_score_to_pass + ""
                }
            }

            let { data } = await axios.post(`${configData.config.API_URL}/api/projects/update/passing/criteria/${moduleUUID}`, _bodyData, {
                headers: {
                    Authorization: `Bearer ${userData.token}`,
                    "Content-Type ": "application/json"
                }
            });

            let _passingCriteriaData = data.data.passingCriteria;
            handleSaveRanges(_passingCriteriaData);

        } catch (error) {
            console.log(error);
            setErrorMsg("Something went wrong.");
        } finally {
            setLoading(false);
            setTimeout(() => {
                setErrorMsg(null);
            }, 2500);
        }

    }

    const handleCancelClick = () => {
        return navigate(`/update/configuration/${userData.user.uuid}`);
    }

    return (
        <>

            <div className="container-fluid bg-grey-main dashboard p-3 pt-2">
                <p style={{ background: '#fff', marginBottom: "0", padding: '20px', fontSize: '14px', borderRadius: '10px', color: '#000', marginTop: '5px' }}>
                    Customize the passing criteria for assigned courses based on your organization's unique requirements. Modify the following factors:<br />
                    <strong>Criteria Type:</strong> Choose the combination of factors for evaluation.<br />
                    <strong>Time Deviation:</strong> Set an acceptable time variation from the ideal completion time (e.g., 20% allows 80%-120% of ideal time).<br />
                    <strong>Step Deviation:</strong> Define the maximum acceptable error percentage (e.g., 20% means a maximum of 20% incorrect steps are allowed).<br />
                    <strong>Minimum Passes:</strong> Set the minimum number of times the user has to pass the course before achieving the certificate (e.g., 2 times).<br />
                    <br />
                    Please note that some modules have <strong>"Critical Steps."</strong> Users must complete all critical steps for successful evaluation, even if they meet the Step Deviation criterion.
                    Customize now to align course evaluations with your organization's specific goals and learning objectives.<br />
                </p>


                <div className="row p-3 pt-0 pb-0">
                    <div className="col-md-6 mt-3 d-flex align-items-start justify-content-start flex-column ">

                        <div className="mt-3">
                            {moduleName && <p>Module name: {moduleName}</p>}
                        </div>
                        <div className='d-flex gap-2 w-50'>
                            <div className="quesformbox mt-3">
                                <p>Minimum Passes</p>
                                <input name="minimumSuccess" value={minimumSuccess} onChange={(e) => setMinimumSuccess((e.target.value).replace(/[^0-9]/g, ''))} placeholder="Minimum Passes" type="text" className="input mt-2" ></input>
                            </div>
                            <div className="quesformbox mt-3">
                                <p>Max Hint Allowed</p>
                                <input name="maxHintAllowed" value={maxHintAllowed} onChange={(e) => setMaxHintAllowed((e.target.value).replace(/[^0-9]/g, ''))} placeholder="Hint Allowed" type="text" className="input mt-2" ></input>
                            </div>
                        </div>

                        {ranges.map((range, index) => (
                            <div key={index} className="range-here">
                                <div>
                                    <span>{range.key} Weightage</span>
                                    <span className="for-percentage">
                                        <input
                                            type="number"
                                            id={range.id}
                                            name="inputBar"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={parseInt((range.value) * 100)}
                                            onChange={(e) => handleChange(index, parseInt(e.target.value))}
                                        />
                                        <span className="percentage-view">%</span>
                                    </span>
                                </div>
                                <div className="exact-slider">
                                    <Slider
                                        value={parseInt((range.value) * 100)}
                                        aria-label="Default"
                                        valueLabelDisplay="auto"
                                        onChange={(event, newValue) => handleChange(index, parseInt(newValue))}
                                    />
                                </div>
                            </div>
                        ))}

                        <div className="range-here">
                            <div>
                                <span style={{ fontSize: "18px" }}>  OVERALL PASSING PERCENTAGE </span>
                                <span className="for-percentage">
                                    <input
                                        type="number"
                                        name="inputBar"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={min_score_to_pass}
                                        onChange={(e) => setMin_score_to_pass(e.target.value)}
                                    />
                                    <span className="percentage-view">%</span>
                                </span>
                            </div>

                        </div>

                        {error && <span className='error-msg'> {error}  </span>}
                    </div>
                    <div className="col-md-6 d-flex align-items-center justify-content-start " >
                        <RangeChart ranges={ranges} />
                    </div>
                </div>

                <div className="row mt-0">
                    <div className="col-12">
                        <div className="footerbtn">

                            {errorMsg && <p className="errorMessage" > {errorMsg} </p>}

                            <button type="button" className="cancel" onClick={handleCancelClick}>
                                Cancel
                            </button>

                            {loading ?
                                <button className="loading save">
                                    <svg viewBox="25 25 50 50">
                                        <circle r="20" cy="50" cx="50"></circle>
                                    </svg>
                                </button>
                                :
                                (userPlan && userPlan["certificateConfig"].passingCriteria) ?
                                    <button type="button" className="save" onClick={() => ShowSetCriteria(true)}>
                                        Save
                                    </button>
                                    :
                                    <button type="button" className="save" onClick={() => setOpenPro(true)}>
                                        <span className='pe-0'><CrownIcon /></span> Save
                                    </button>
                            }

                        </div>
                    </div>
                </div>

            </div>

            <ConfirmBox title={"Set Passing Criteria"} message={"Do you really want to save this criteria?"} show={setCriteria} handleSubmit={() => SaveCriteria()} handleClose={() => ShowSetCriteria(false)} deleteBtn={false} />
            <ProPopup openPro={openPro} setOpenPro={setOpenPro} />

        </>
    )
}
export default EditCriteria